import { AfterViewInit, Directive, ElementRef, HostListener, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import * as d3 from 'd3';
import { Color, Utils } from '../shared/utils';
import { TooltipOptions } from '../tooltip/tooltip.component';

export class ChartOptions {
    colors: Color[];
    selectedGroup?: string;
    selectedSubgroup?: string;
}

export type BarData = {
    key: string;
    value: string;
    tooltip?: TooltipOptions;
}

@Directive()
export abstract class MproChart<T, K extends ChartOptions = ChartOptions> implements OnInit, OnChanges, AfterViewInit {
    public chartId: string = Utils.generateId(5);
    public tooltipOptions: TooltipOptions = {
        type: 'chart',
        title: 'Chart Tooltip',
        chartText: 'Here is the main information',
        chartCaption: 'Caption information'
    };
    @Input() public data: T;
    @Input() public options: K;
    @ViewChild('tooltip') public tooltip: ElementRef;
    protected svg: any;
    protected width: number;
    protected height: number;
    protected abstract margin: { top: number, right: number, bottom: number, left: number };

    constructor(protected el: ElementRef) { }

    @HostListener('window:resize', ['$event'])
    public onResize(event: any) {
        if (!this.updateSize()) {
            // there are many window resize events and the width and height of the
            // container are not always changed
            return;
        }

        if (this.svg && this.hasData()) {
            d3.selectAll(`#${this.chartId} > *`).remove(); // clear chart
            this._createSvg();
            this._drawChart();
        }
    }

    public ngOnInit() {
        this.updateSize();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (this.svg && changes.data?.currentValue.length > 0 ||
            this.svg && this.hasData() && Object.keys(changes.options?.currentValue).length > 0) {
            d3.selectAll(`#${this.chartId} svg g > *`).remove(); // clear chart
            this._drawChart();
        }
    }

    public ngAfterViewInit(): void {
        this._createSvg();
        this._drawChart();
    }

    protected updateSize(): boolean {
        const parentElement = this.el.nativeElement.getBoundingClientRect();
        let newWidth = parentElement.width - this.margin.left - this.margin.right;
        let newHeight = parentElement.height - this.margin.top - this.margin.bottom;
        if (this.width === newWidth && this.height === newHeight) {
            return false;
        }

        this.width = newWidth;
        this.height = newHeight;
        return true;
    }

    protected selectGroup(group: string): void {
        d3.selectAll(`#${this.chartId} g.group:not(.${CSS.escape(group)})`).attr('fill-opacity', '25%');
    }

    protected selectSubgroup(subgroup: string): void {
        d3.selectAll(`#${this.chartId} rect.subgroup:not(.${CSS.escape(subgroup)})`).attr('fill-opacity', '25%');
    }

    protected getColorsHex(): string[] {
        return this.options.colors.map(color => Utils.getColorHex(color));
    }

    protected abstract drawChart(): void;

    protected abstract hasData(): boolean;

    private _createSvg(): void {
        this.svg = d3
            .select(`figure#${this.chartId}`)
            .append('svg')
            .attr('width', this.width + this.margin.left + this.margin.right)
            .attr('height', this.height + this.margin.top + this.margin.bottom)
            .append('g')
            .attr('transform', `translate(${this.margin.left}, ${this.margin.top})`);
    }

    protected _drawChart(): void {
        if (!this.hasData() || !this.options) {
            return;
        }

        this.drawChart();

        d3.selectAll(`#${this.chartId} g line`)
            .attr('stroke', '#E4E4E4');

        this.attachTooltip();

        if (this.options.selectedGroup) {
            this.selectGroup(this.options.selectedGroup);
        }

        if (this.options.selectedSubgroup) {
            this.selectSubgroup(this.options.selectedSubgroup);
        }
    }

    private attachTooltip(): void {
        d3.selectAll(`#${this.chartId} rect[width][height]`)
            .on('mousemove', (d: any) => this.toggleTooltip(d))
            .on('mouseleave', () => this.toggleTooltip());

        d3.selectAll(`#${this.chartId} .tooltipTrigger`)
            .on('mousemove', (d: any) => this.toggleTooltip(d))
            .on('mouseleave', () => this.toggleTooltip());
    }

    private toggleTooltip(target?: any) {
        if (!target || !target.currentTarget.__data__.tooltip) {
            this.tooltip.nativeElement.hidden = true;
            return;
        }

        // offset tooltip 2 pixels otherwise it goes under the cursor which hides it
        // even if cursor is over a bar
        this.tooltipOptions = { ...target.currentTarget.__data__.tooltip };
        this.tooltip.nativeElement.style.left = (target.offsetX + 5) + 'px';
        this.tooltip.nativeElement.style.top = (target.offsetY + 5) + 'px';
        this.tooltip.nativeElement.hidden = false;
    }
}